/**
 * Pure scenario engine. No I/O, no timers — the Durable Object drives it and persists `RunState`.
 */
import type { DecisionNode, DecisionOption, OptionQuality, Scenario, ScenarioNode } from "./schema";

export interface DecisionRecord {
  nodeId: string;
  /** Winning option id (after tie-breaking). */
  optionId: string;
  quality: OptionQuality;
  /** optionId → weighted vote count. */
  tally: Record<string, number>;
  totalVotes: number;
  eligibleVoters: number;
  /** Milliseconds the vote stayed open. */
  openMs: number;
  resolvedAt: number;
  /** True when the facilitator forced the outcome instead of the vote. */
  overridden: boolean;
}

export interface RunState {
  nodeId: string;
  vars: Record<string, number>;
  /** dimension → points earned so far. */
  earned: Record<string, number>;
  /** dimension → maximum points obtainable on the decisions visited so far. */
  possible: Record<string, number>;
  /** Ordered list of visited node ids (includes the current one). */
  path: string[];
  decisions: DecisionRecord[];
  injectsFired: string[];
  /** Peak value observed per variable (for badges like "trust never exceeded X"). */
  varPeaks: Record<string, number>;
  finished: boolean;
}

export function createRun(scenario: Scenario): RunState {
  const vars = { ...scenario.variables };
  const earned: Record<string, number> = {};
  const possible: Record<string, number> = {};
  for (const d of scenario.dimensions) {
    earned[d.id] = 0;
    possible[d.id] = 0;
  }
  return {
    nodeId: scenario.start,
    vars,
    earned,
    possible,
    path: [scenario.start],
    decisions: [],
    injectsFired: [],
    varPeaks: { ...vars },
    finished: scenario.nodes[scenario.start]?.type === "debrief",
  };
}

export function currentNode(scenario: Scenario, state: RunState): ScenarioNode {
  const node = scenario.nodes[state.nodeId];
  if (!node) throw new Error(`Run is at unknown node "${state.nodeId}"`);
  return node;
}

function enter(scenario: Scenario, state: RunState, nodeId: string): RunState {
  const node = scenario.nodes[nodeId];
  if (!node) throw new Error(`Cannot enter unknown node "${nodeId}"`);
  let next: RunState = { ...state, nodeId, path: [...state.path, nodeId], finished: node.type === "debrief" };
  // Branch nodes resolve immediately and are recorded in the path for the report.
  if (node.type === "branch") {
    const target = resolveBranch(node, next.vars);
    next = enter(scenario, next, target);
  }
  return next;
}

export function resolveBranch(
  node: Extract<ScenarioNode, { type: "branch" }>,
  vars: Record<string, number>,
): string {
  for (const c of node.conditions) {
    const v = vars[c.var] ?? 0;
    const hit =
      (c.op === ">" && v > c.value) ||
      (c.op === ">=" && v >= c.value) ||
      (c.op === "<" && v < c.value) ||
      (c.op === "<=" && v <= c.value) ||
      (c.op === "==" && v === c.value) ||
      (c.op === "!=" && v !== c.value);
    if (hit) return c.next;
  }
  return node.else;
}

/** Advance from a linear node (narrative, call, reveal, poll). Returns the same state at a debrief or dead end. */
export function advance(scenario: Scenario, state: RunState): RunState {
  const node = currentNode(scenario, state);
  if (node.type === "decision") throw new Error("advance() called on a decision node; use resolveDecision()");
  if (node.type === "debrief" || node.type === "branch") return state;
  if (!node.next) return state;
  return enter(scenario, state, node.next);
}

/** Jump anywhere (facilitator "go to" or inject). */
export function jump(scenario: Scenario, state: RunState, nodeId: string): RunState {
  return enter(scenario, state, nodeId);
}

export function fireInject(scenario: Scenario, state: RunState, injectId: string): RunState {
  const inject = scenario.injects.find((i) => i.id === injectId);
  if (!inject) throw new Error(`Unknown inject "${injectId}"`);
  const next = enter(scenario, state, inject.node);
  return { ...next, injectsFired: [...next.injectsFired, injectId] };
}

export function availableInjects(scenario: Scenario, state: RunState): string[] {
  return scenario.injects
    .filter((i) => !state.injectsFired.includes(i.id))
    .filter((i) => i.availableAfter.length === 0 || i.availableAfter.some((n) => state.path.includes(n)))
    .map((i) => i.id);
}

/**
 * Pick the winning option from a tally. Ties break toward the *worse* option: hesitation in a
 * real crisis favours the attacker, and it keeps the outcome deterministic.
 */
export function pickWinner(node: DecisionNode, tally: Record<string, number>): DecisionOption {
  const rank: Record<OptionQuality, number> = { bad: 0, risky: 1, good: 2, best: 3 };
  let winner: DecisionOption | undefined;
  let winnerVotes = -1;
  for (const opt of node.options) {
    const votes = tally[opt.id] ?? 0;
    if (
      votes > winnerVotes ||
      (votes === winnerVotes && winner && rank[opt.quality] < rank[winner.quality])
    ) {
      winner = opt;
      winnerVotes = votes;
    }
  }
  if (!winner) throw new Error("decision node has no options");
  return winner;
}

export interface ResolveInput {
  tally: Record<string, number>;
  totalVotes: number;
  eligibleVoters: number;
  openMs: number;
  now: number;
  /** Force a specific option regardless of the tally (facilitator override). */
  forceOptionId?: string;
}

export function resolveDecision(
  scenario: Scenario,
  state: RunState,
  input: ResolveInput,
): { state: RunState; option: DecisionOption; record: DecisionRecord } {
  const node = currentNode(scenario, state);
  if (node.type !== "decision") throw new Error("resolveDecision() called on a non-decision node");

  const option = input.forceOptionId
    ? node.options.find((o) => o.id === input.forceOptionId)
    : pickWinner(node, input.tally);
  if (!option) throw new Error(`Unknown option "${input.forceOptionId}"`);

  const vars = { ...state.vars };
  const varPeaks = { ...state.varPeaks };
  for (const [k, delta] of Object.entries(option.effects.vars)) {
    vars[k] = (vars[k] ?? 0) + delta;
    varPeaks[k] = Math.max(varPeaks[k] ?? Number.NEGATIVE_INFINITY, vars[k]);
  }
  const earned = { ...state.earned };
  const possible = { ...state.possible };
  for (const d of scenario.dimensions) {
    const best = Math.max(0, ...node.options.map((o) => o.effects.score[d.id] ?? 0));
    possible[d.id] = (possible[d.id] ?? 0) + best;
    earned[d.id] = (earned[d.id] ?? 0) + (option.effects.score[d.id] ?? 0);
  }

  const record: DecisionRecord = {
    nodeId: state.nodeId,
    optionId: option.id,
    quality: option.quality,
    tally: input.tally,
    totalVotes: input.totalVotes,
    eligibleVoters: input.eligibleVoters,
    openMs: input.openMs,
    resolvedAt: input.now,
    overridden: Boolean(input.forceOptionId),
  };

  const withRecord: RunState = {
    ...state,
    vars,
    varPeaks,
    earned,
    possible,
    decisions: [...state.decisions, record],
  };
  return { state: enter(scenario, withRecord, option.next), option, record };
}

/** Dimension percentages 0..100 (null when no decision touched the dimension yet). */
export function dimensionScores(scenario: Scenario, state: RunState): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const d of scenario.dimensions) {
    const p = state.possible[d.id] ?? 0;
    const e = state.earned[d.id] ?? 0;
    out[d.id] = p > 0 ? Math.round(Math.max(0, Math.min(1, e / p)) * 100) : null;
  }
  return out;
}

/** Team readiness score: mean of dimension percentages that were exercised. */
export function readinessScore(scenario: Scenario, state: RunState): number | null {
  const dims = Object.values(dimensionScores(scenario, state)).filter((v): v is number => v !== null);
  if (!dims.length) return null;
  return Math.round(dims.reduce((a, b) => a + b, 0) / dims.length);
}

/* ------------------------------------------------------------------------------------------ */
/* Player scoring                                                                              */
/* ------------------------------------------------------------------------------------------ */

export const QUALITY_POINTS: Record<OptionQuality, number> = { best: 100, good: 60, risky: 20, bad: 0 };
export const SPEED_BONUS_MAX = 25;
export const STREAK_BONUS = 10;
export const ROLE_BONUS = 25;

export interface VoteScoreInput {
  quality: OptionQuality;
  /** 0..1 fraction of the timer remaining when the vote was cast. */
  remainingFraction: number;
  /** Consecutive best/good picks before this one. */
  streak: number;
  /** Whether the option's roleBonus lists the player's role. */
  roleMatch: boolean;
}

export function scoreVote(input: VoteScoreInput): { points: number; breakdown: Record<string, number> } {
  const base = QUALITY_POINTS[input.quality];
  const speed =
    input.quality === "best" || input.quality === "good"
      ? Math.round(SPEED_BONUS_MAX * Math.max(0, Math.min(1, input.remainingFraction)))
      : 0;
  const streak =
    input.quality === "best" || input.quality === "good" ? Math.min(5, input.streak) * STREAK_BONUS : 0;
  const role = input.roleMatch && input.quality !== "bad" ? ROLE_BONUS : 0;
  return { points: base + speed + streak + role, breakdown: { base, speed, streak, role } };
}

/* ------------------------------------------------------------------------------------------ */
/* Badges                                                                                      */
/* ------------------------------------------------------------------------------------------ */

export interface PlayerVoteFact {
  nodeId: string;
  optionId: string;
  quality: OptionQuality;
  tags: string[];
  remainingFraction: number;
  withMajority: boolean;
}

export function playerBadges(scenario: Scenario, state: RunState, votes: PlayerVoteFact[]): string[] {
  const out: string[] = [];
  for (const b of scenario.badges) {
    const c = b.condition;
    let hit = false;
    switch (c.kind) {
      case "optionTag":
        hit = votes.filter((v) => v.tags.includes(c.tag)).length >= c.min;
        break;
      case "speed":
        hit =
          votes.length >= c.minVotes &&
          votes.reduce((a, v) => a + (1 - v.remainingFraction), 0) / votes.length <= c.fraction;
        break;
      case "consensus":
        hit =
          votes.length >= c.minVotes &&
          votes.filter((v) => v.withMajority).length / votes.length >= c.fraction;
        break;
      case "bestPicks":
        hit = votes.filter((v) => v.quality === "best").length >= c.min;
        break;
      case "teamVarBelow":
        hit = (state.varPeaks[c.var] ?? 0) < c.below;
        break;
      case "teamDimension": {
        const s = dimensionScores(scenario, state)[c.dimension];
        hit = s !== null && s !== undefined && s >= c.min;
        break;
      }
    }
    if (hit) out.push(b.id);
  }
  return out;
}

export function teamBadges(scenario: Scenario, state: RunState): string[] {
  return scenario.badges
    .filter((b) => b.condition.kind === "teamVarBelow" || b.condition.kind === "teamDimension")
    .filter((b) => playerBadges({ ...scenario, badges: [b] }, state, []).length > 0)
    .map((b) => b.id);
}
